import { Client, Room } from 'colyseus.js';
import type { Direction } from '@snake-royalle/shared';
import { getDemoDisplayName } from '../utils/demo-id.js';

const SERVER_URL = (import.meta as any).env?.VITE_SERVER_URL || 'ws://localhost:2567';

/** Manages connection to the Colyseus server */
export class ColyseusClient {
  private client: Client;
  private room: Room | null = null;
  private listeners: Map<string, Set<(data: any) => void>> = new Map();
  /** True once WE asked to leave (extraction / death / match end). Lets the
   *  onLeave handler tell an intentional exit apart from a real drop so we
   *  don't fire a bogus "connection lost" on normal transitions. */
  private leaving = false;

  constructor() {
    this.client = new Client(SERVER_URL);
  }

  /** Fan a synthetic event out to local listeners (same path as room
   *  messages). Used for 'disconnected'. */
  private dispatch(event: string, data: any): void {
    const handlers = this.listeners.get(event);
    if (handlers) for (const handler of handlers) handler(data);
  }

  async joinSinglePlayer(
    perkId: string | null = null,
    entryToken: string | null = null
  ): Promise<{
    snakeId: string;
    gridWidth: number;
    gridHeight: number;
    /** Stake multiplier this entry was bought at (1/2/5/10). Always
     *  defined for paid joins; demo / dev sessions get 1. */
    stakeMultiplier: number;
  }> {
    // Send player identity if logged in
    const currentUser = (window as any).__currentUser;
    this.room = await this.client.joinOrCreate('single_player', {
      perkId,
      playerName: currentUser?.name ?? 'Player',
      playerId: currentUser?.id ?? null,
      provider: currentUser?.provider ?? null,
      entryToken,
    });
    return this.wireRoom();
  }

  /**
   * Anonymous demo room — no auth, no payment, no DB writes server-side.
   * Display name is generated once and persisted in localStorage.
   */
  async joinDemo(perkId: string | null = null): Promise<{
    snakeId: string;
    gridWidth: number;
    gridHeight: number;
    stakeMultiplier: number;
  }> {
    this.room = await this.client.joinOrCreate('demo_player', {
      perkId,
      playerName: getDemoDisplayName(),
    });
    return this.wireRoom();
  }

  /**
   * Tournament room — credit-gated (no real money). One life, separate score.
   * `ticket` is the single-use HMAC issued by POST /api/tournament/enter.
   */
  async joinTournament(
    perkId: string | null = null,
    ticket: string | null = null,
  ): Promise<{ snakeId: string; gridWidth: number; gridHeight: number; stakeMultiplier: number }> {
    const currentUser = (window as any).__currentUser;
    this.room = await this.client.joinOrCreate('tournament', {
      perkId,
      playerName: currentUser?.name ?? 'Player',
      ticket,
    });
    return this.wireRoom();
  }

  /**
   * Read-only OBSERVER join to the live arena (byte iframe embed). No snake is
   * created and no entry token / payment is required — the server skips both
   * when `spectate: true`. The returned `snakeId` is null (no player).
   */
  async joinAsSpectator(): Promise<{ snakeId: string | null; gridWidth: number; gridHeight: number; stakeMultiplier: number }> {
    this.room = await this.client.joinOrCreate('single_player', { spectate: true });
    return this.wireRoom();
  }

  /** Wire message forwarding + resolve once 'joined' fires. Shared by both join paths. */
  private wireRoom(): Promise<{ snakeId: string; gridWidth: number; gridHeight: number; stakeMultiplier: number }> {

    // Forward all room messages to local listeners
    (this.room as any).onMessage('*', (type: string | number, message: unknown) => {
      this.dispatch(String(type), message);
    });

    // Surface unexpected drops/errors so the scene can stop the silent
    // freeze. onLeave fires on ANY close — guard with `leaving` so our own
    // disconnect() (extraction/death/match end) doesn't trip it.
    this.room!.onLeave((code: number) => {
      if (this.leaving) return;
      this.dispatch('disconnected', { reason: 'closed', code });
    });
    this.room!.onError((code: number, message?: string) => {
      this.dispatch('disconnected', { reason: 'error', code, message });
    });

    // Wait for the 'joined' message. Demo / pre-multiplier servers
    // don't send the field — default to 1 so the client renders
    // unchanged in that path.
    return new Promise((resolve) => {
      this.room!.onMessage('joined', (data: any) => {
        const stakeMultiplier =
          typeof data?.stakeMultiplier === 'number' && data.stakeMultiplier > 0
            ? data.stakeMultiplier
            : 1;
        resolve({ ...data, stakeMultiplier });
      });
    });
  }

  private safeSend(type: string, data?: any): void {
    try {
      this.room?.send(type, data);
    } catch {
      // WebSocket may be closing — ignore
    }
  }

  sendInput(direction: Direction): void {
    this.safeSend('input', { direction });
  }

  sendReenter(): void {
    this.safeSend('reenter');
  }

  sendCancelReentry(): void {
    this.safeSend('cancel_reentry');
  }

  sendLatencyPing(clientSentAt: number): void {
    this.safeSend('latency_ping', { clientSentAt });
  }

  isConnected(): boolean {
    return this.room !== null;
  }

  on(event: string, callback: (data: any) => void): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);
  }

  off(event: string, callback: (data: any) => void): void {
    this.listeners.get(event)?.delete(callback);
  }

  disconnect(): void {
    this.leaving = true;
    this.room?.leave();
    this.room = null;
  }
}
